import { setAppLoading } from "actions/appStateActions";
import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { EMPTY_WALLET_ITEMS } from "actions/walletActions";
import IamApi from "api/IamApi";
import { postPayableProcessIntent } from "api/IamApi/entities/ezpay";
import circleInfo from "assets/icons/circleInfo.png";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { PageLoader } from "components/ezpay/common/page-loader";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";
import {
  KUBRA_PRIVACY_POLICY_URL,
  KUBRA_TERMS_OF_USE_URL,
} from "constants/externalUrls";
import FundingOption from "constants/FundingOption";
import {
  PAYABLE_REVIEW_NAMESPACE,
  RECEIPT_INFO_MODAL_NAMESPACE,
} from "constants/i18n-namespaces";
import { isSuccessfulPayment } from "constants/paymentStatus";
import { useToast } from "context/ToastContext";
import { useFormik } from "formik";
import { useCurrencyFormatter } from "hooks";
import i18n from "i18n/I18nApp";
import type { AnalyticsEvent } from "kubra-ui-lib-mfe";
import { AnalyticsEventType, useTracking } from "kubra-ui-lib-mfe";
import {
  Banner,
  BannerProps,
  Button,
  Checkbox,
  InputField,
  TextButton,
  Toggle,
} from "kubra-ux-forge";
import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useOutletContext } from "react-router";
import routing from "routing";
import { getBiller } from "selectors";
import { getDeviceFingerprint, getIsMobile } from "selectors/appState";
import { getLabelsWithKrn } from "selectors/labelsWithKrn";
import { getPayableSession } from "selectors/payableSession";
import { getPaymentIntent } from "selectors/paymentIntent";
import { PaymentIntentProcessRequest } from "types/Api/Payment/paymentIntentProcessRequest";
import { ExtensionFieldValue } from "types/payable";
import { IPayableSession } from "types/payableSession";
import { getBannerRole } from "utilities/banner-accessibility";
import {
  getCheckboxLabelClass,
  getDomain,
  getErrorText,
  getLabelWithKrn,
  mapUiLanguageToSessionLanguage,
  phoneFormat,
} from "utilities/helper";
import validator from "validator";
import * as Yup from "yup";
import { IReceiptInfo } from "../common/receipt-info-modal";
import { PayableReviewSummary } from "../payable-review-summary";

function getExtensionFieldValue(field: ExtensionFieldValue) {
  if (field.type === "PhoneNumber") {
    const phonePrefix = "tel:";
    return field.value.startsWith(phonePrefix)
      ? field.value.slice(phonePrefix.length)
      : field.value;
  } else if (field.type === "Email") {
    const emailPrefix = "mailto:";
    return field.value.startsWith(emailPrefix)
      ? field.value.slice(emailPrefix.length)
      : field.value;
  } else if (
    field.type === "Address" &&
    typeof field.value === "object" &&
    field.value !== null
  ) {
    const { streetAddress, locality, region, postalCode } = field.value;
    return [streetAddress, locality, region, postalCode]
      .filter(Boolean)
      .join(", ");
  } else {
    return field.value;
  }
}

const receiptInfoInitialState: IReceiptInfo = {
  email: "",
  mobileNumber: "",
  emailAlert: false,
  mobileAlert: false,
  alertEnabled: false,
};

export const PayableReviewPage = () => {
  const { t } = useTranslation(PAYABLE_REVIEW_NAMESPACE);
  const isMobile = useSelector(getIsMobile);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const deviceFingerprint = useSelector(getDeviceFingerprint);

  const biller = useSelector(getBiller);

  const { applePaySession } = useOutletContext<IPayableSession>();
  const payableSession = useSelector(getPayableSession);

  const labelSourceWithKrn = useSelector(getLabelsWithKrn) as {
    labelsWithKrn: any;
  };
  const currencyFormatter = useCurrencyFormatter();

  const { trackEvent } = useTracking<Partial<AnalyticsEvent>>();

  const paymentIntentState = useSelector(getPaymentIntent);
  const bankAccount = paymentIntentState?.bankAccount;
  const creditDebitCard = paymentIntentState?.creditDebitCard;
  const wallet = paymentIntentState?.wallet;
  const paymentMethod = paymentIntentState?.paymentMethod;
  const digitalWallet = paymentIntentState?.digitalWallet;
  const [bannerState, setBannerState] = useState<BannerProps>({
    active: false,
    variant: "info",
    messageLabel: "",
  });

  const { addToast } = useToast();

  useEffect(() => {
    // scroll to top when banner shows up
    if (bannerState.active) {
      // scrolls to top in Safari
      document.body.scrollTop = 0;
      // scrolls to top in Chrome, Firefox, IE, and Opera
      document.documentElement.scrollTop = 0;
      // Mobile view
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [bannerState.active]);

  const isDigitalWalletPayment =
    paymentIntentState.paymentMethod == FundingOption.GooglePay ||
    paymentIntentState.paymentMethod == FundingOption.ApplePay ||
    paymentIntentState.paymentMethod == FundingOption.PayPal;

  function getValidationSchema(
    isDigitalWallet: boolean,
    t: any,
    validateEmail: (v: string) => boolean,
    validateMobileNumber: (v: string | null | undefined) => boolean,
  ) {
    if (isDigitalWallet) return undefined;
    return Yup.object({
      email: Yup.string()
        .nullable()
        .optional()
        .test("email", t("email-invalid"), function (email) {
          if (!email) return true;
          return validateEmail(email);
        }),
      mobileNumber: Yup.string()
        .nullable()
        .optional()
        .max(14, t("mobile-number-invalid"))
        .test(
          "mobileNumber",
          t("mobile-number-invalid"),
          function (mobileNumber) {
            return validateMobileNumber(mobileNumber);
          },
        ),
      alertEnabled: Yup.bool(),
      emailAlert: Yup.bool(),
      mobileAlert: Yup.bool(),
    });
  }

  const formik = useFormik({
    initialValues: { ...receiptInfoInitialState },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getValidationSchema(
      isDigitalWalletPayment,
      t,
      validateEmail,
      validateMobileNumber,
    ),
    onSubmit: (values: IReceiptInfo, actions: any) => {
      actions.setSubmitting(true);

      // We're not displaying the email and phone number input fields for digital wallet payments (for some reason.)
      // So we need to get them from the payment intent state. They're set in the payment intent state on the PayableDashboard page.
      if (isDigitalWalletPayment) {
        values.email = paymentIntentState?.receiptInfo?.email ?? "";
        values.mobileNumber =
          paymentIntentState?.receiptInfo?.mobileNumber ?? "";
      }

      if (!isDigitalWalletPayment && !values.email && !values.mobileNumber) {
        setBannerState((prev) => ({
          ...prev,
          active: true,
          variant: "error",
          messageLabel: t("email-or-phone-required-error"),
          withClose: true,
          withHyperlink: false,
          withIcon: true,
        }));

        actions.setSubmitting(false);
        return;
      }

      makePayment(values);
    },
  });

  const {
    values,
    touched,
    errors,
    setFieldValue,
    setSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
  } = formik;

  function validateEmail(value: string) {
    if (value == undefined || value == null || value === "") {
      return false;
    } else {
      return validator.isEmail(value);
    }
  }

  function validateMobileNumber(value: string | null | undefined) {
    if (value == undefined || value == null || value === "") {
      return true; // mobile number is optional
    } else {
      return validator.isMobilePhone(value, "en-US");
    }
  }

  const handleToggleChange = (
    event: SyntheticEvent<HTMLInputElement, Event>,
  ) => {
    const newToggleValue = event.currentTarget.checked;
    setFieldValue("alertEnabled", newToggleValue);

    if (newToggleValue) {
      setFieldValue("emailAlert", true);
      if (errors.mobileNumber == undefined && values.mobileNumber != "") {
        setFieldValue("mobileAlert", true);
      } else {
        setFieldValue("mobileAlert", false);
      }
    } else {
      setFieldValue("emailAlert", false);
      setFieldValue("mobileAlert", false);
    }
  };

  const handleEmailAlertChange = (
    event: SyntheticEvent<HTMLInputElement, Event>,
  ) => {
    const newToggleValue = event.currentTarget.checked;
    if (!newToggleValue && !values.mobileAlert) {
      setFieldValue("alertEnabled", false);
    }

    setFieldValue("emailAlert", newToggleValue);
  };

  const handleMobileAlertChange = (
    event: SyntheticEvent<HTMLInputElement, Event>,
  ) => {
    const newToggleValue = event.currentTarget.checked;
    if (!values.emailAlert && !newToggleValue) {
      setFieldValue("alertEnabled", false);
    }

    setFieldValue("mobileAlert", newToggleValue);
  };

  const handleMobileNumberChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const regex = /^[0-9\b()-]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value.replace(/[^\d]/g, ""))
    ) {
      if (event.currentTarget.value.length < 15) {
        const mobNo = phoneFormat(event.currentTarget.value);
        await setFieldValue("mobileNumber", mobNo);
      }
    }
  };

  // TODO: review if we need to reinitiate payment intent after payment success with the new generic payables flow.
  // We likely can't initiate a payment because a payable needs to be selected first. So will probably have to remove this.
  const reInitiatePaymentIntent = async () => {
    try {
      if (
        biller !== null &&
        biller !== undefined &&
        payableSession.payable?.krn
      ) {
        await IamApi.ezpay.postPayableInitiateIntent(
          payableSession.payable?.krn,
          deviceFingerprint,
          navigator.userAgent,
        );
        const domain = getDomain();
        const newPayableSession =
          await IamApi.ezpay.getPayableFromSession(domain);

        dispatch(setPayableSession(newPayableSession));
      }
    } catch (error) {
      console.error("Error occurred initiating new intent: ", error);
    }
  };

  const getProcessRequest = (
    receiptInfo: IReceiptInfo,
  ): PaymentIntentProcessRequest | null => {
    const mobileAlertEnabled = receiptInfo?.alertEnabled
      ? receiptInfo.emailAlert
      : false;

    const smsAlertEnabled = receiptInfo?.alertEnabled
      ? receiptInfo.mobileAlert && receiptInfo.mobileNumber.length > 0
      : false;

    const processRequest: PaymentIntentProcessRequest = {
      email: receiptInfo.email,
      phoneNumber: receiptInfo.mobileNumber.replace(/[()\s-]/g, ""),
      alerts: {
        emailAlert: mobileAlertEnabled,
        smsAlert: smsAlertEnabled,
      },
      fundingOption: paymentMethod,
    };

    if (paymentMethod === FundingOption.Card && creditDebitCard) {
      processRequest.card = {
        card: creditDebitCard.cardNo.replace(/\s/g, ""),
        cvc: creditDebitCard.securityCode,
        cardType: creditDebitCard.cardType,
        expiry: creditDebitCard.cardExpiry,
        zip: creditDebitCard.zipCode,
        holderName: creditDebitCard.firstName + " " + creditDebitCard.lastName,
        saveToWallet: creditDebitCard.saveToWallet,
      };
    } else if (paymentMethod === FundingOption.BankAccount && bankAccount) {
      processRequest.bank = {
        bankNumber: bankAccount.bankAccountNumber,
        routingNumber: bankAccount.routingNumber,
        accountType: bankAccount.accountType,
        holderName: bankAccount.accountOwnerName,
        saveToWallet: bankAccount.saveToWallet,
      };
    } else if (paymentMethod === FundingOption.Wallet && wallet) {
      processRequest.wallet = {
        fundingSourceId: wallet.fundingSourceId,
        type: wallet.fundingOption,
      };
    } else if (digitalWallet) {
      processRequest.digitalWallet = {
        paymentToken: digitalWallet.paymentToken,
        cardType: digitalWallet.cardType,
      };
    } else {
      console.error(`Unknown payment method ${paymentMethod}`);
      return null;
    }

    return processRequest;
  };

  const trackPaymentEvent = (
    fundingSourceType: string,
    wallet: string,
    success: string,
  ) => {
    trackEvent({
      type: AnalyticsEventType.customEvent,
      event: `payment_${success}_${fundingSourceType.toLowerCase()}_${wallet}`,
    });
  };

  const editPayment = async () => {
    try {
      const payableType = payableSession.payableType;
      const payable = payableSession.payable;

      if (!payable?.krn) {
        throw new Error("Payable KRN is unavailable in the session.");
      }

      await IamApi.ezpay.logout();

      // TODO: dispatching USER_LOGOUT wipes out all state in the redux store.
      // This causes few problems:
      // 1. forces refetching of the biller configuration (causing unnecessary re-renders.)
      // 2. the ProtectedRoute checks for a payable session and navigates to the index page, if it doesn't exist.
      // dispatch({ type: USER_LOGOUT });
      // dispatch(setPayableSession(undefined));

      // To be able to make a another payment for the same payable, we need to create
      // a new session (EZ-PAY API) by calling the validate payable endpoint and then
      // initiating a payment intent for the same payable.

      // Grab the first lookup from the payable type and get the field values from the payable.
      const lookup = payableType?.lookups?.[0];
      const lookupFields: Record<string, string> = lookup
        ? lookup.fields.reduce(
            (fieldValues, field) => ({
              ...fieldValues,
              [field]: payable.lookupFields[field]?.value ?? "",
            }),
            {} as Record<string, string>,
          )
        : {};

      // For account payables, the account number is not part of the lookup fields, so add it manually.
      if (payable.category === "account") {
        lookupFields["accountNumber"] = payable.accountNumber;
      }

      const domain = getDomain();
      const lookupRequest = {
        typeKrn: payableType.typeKrn,
        lookupFields: { ...lookupFields },
        domain: domain,
        channel: "Web",
        language: i18n.language,
      };

      await IamApi.ezpay.postLookups(biller.spaceKrn, lookupRequest);

      await IamApi.ezpay.postPayableInitiateIntent(
        payable.krn,
        deviceFingerprint,
        navigator.userAgent,
      );

      const newPayableSession =
        await IamApi.ezpay.getPayableFromSession(domain);

      dispatch(setPayableSession(newPayableSession));
      dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
      dispatch({ type: EMPTY_WALLET_ITEMS });

      navigate(routing.payableDashboard.index());
    } catch (error) {
      console.error(
        "Error while trying to create session for making another payment",
        error,
      );

      navigate(routing.index());
    }
  };

  const handleProcessPaymentIntentFailed = (
    processRequest: PaymentIntentProcessRequest,
  ) => {
    dispatch(setAppLoading(false));
    setBannerState({
      active: true,
      variant: "error",
      messageLabel: t("payment-failed"),
      messageBody: t("process-payment-error"),
      withIcon: true,
      withClose: true,
      withHyperlink: true,
      hyperlinkText: t("edit-payment"),
      hyperlink: () => {
        editPayment();
      },
    });
    trackPaymentEvent(
      processRequest.fundingOption,
      processRequest.wallet ? "wallet" : "adhoc",
      "failure",
    );
    applePaySession?.completePayment(window.ApplePaySession.STATUS_FAILURE);
  };

  const saveWalletToast = (fundingOption: string) => {
    let message = "";
    if (fundingOption === FundingOption.BankAccount) {
      message = t("bank-saved-toast", { ns: PAYABLE_REVIEW_NAMESPACE });
    } else if (fundingOption === FundingOption.Card) {
      message = t("card-saved-toast", { ns: PAYABLE_REVIEW_NAMESPACE });
    }
    if (!message) {
      return;
    }
    addToast(message, "success");
  };

  const makePayment = (receiptInfo: IReceiptInfo) => {
    // TODO: review if we need to dispatch this action for payable flow.
    // It seems like the only place where the receipt info is used is in the receipt info modal.
    // Which is not used in the payable flow.
    dispatch({ type: ACTION.ADD_RECEIPTINFO, payload: receiptInfo });

    dispatch(
      setAppLoading(
        true,
        t("payment-processing-loader-title"),
        t("payment-processing-loader-subText"),
      ),
    );

    // Get process request payload.
    const processRequest = getProcessRequest(receiptInfo);
    if (processRequest === null) {
      return;
    }

    postPayableProcessIntent(
      processRequest,
      mapUiLanguageToSessionLanguage(i18n.language),
    )
      .then((res) => {
        if (res.status == 200 && isSuccessfulPayment(res.data.status)) {
          dispatch(setAppLoading(false));
          dispatch({
            type: ACTION.ADD_PAYMENT_STATUS,
            payload: res.data.status,
          });
          dispatch({
            type: ACTION.ADD_PAYMENT_CONFIRMATION_NUMBER,
            payload: res.data.paymentId,
          });

          trackPaymentEvent(
            processRequest.fundingOption,
            processRequest.wallet ? "wallet" : "adhoc",
            "success",
          );

          navigate(routing.paymentSuccess.index());

          applePaySession?.completePayment(
            window.ApplePaySession.STATUS_SUCCESS,
          );
          setSubmitting(false);
          // TODO: review logic for reinitiating payment intent. Do we just reinitiate the intent or create a new session?
          // reInitiatePaymentIntent();

          const isSaved = res?.data?.walletResponse?.isSaved;
          if (
            isSaved &&
            ["Card", "BankAccount"].includes(processRequest?.fundingOption)
          ) {
            saveWalletToast(processRequest.fundingOption);
          }
        } else {
          handleProcessPaymentIntentFailed(processRequest);

          // TODO: review logic for reinitiating payment intent. Do we just reinitiate the intent or create a new session?
          // reInitiatePaymentIntent(); commenting out due to questions around reinitiating and the flow in errors
          setSubmitting(true);
        }
      })
      .catch(() => {
        handleProcessPaymentIntentFailed(processRequest);
        setBannerState((prev) => ({
          ...prev,
          active: true,
          variant: "error",
          messageLabel: t("payment-failed"),
          messageBody: t("process-payment-error"),
          withClose: true,
          withHyperlink: true,
          hyperlinkText: t("edit-payment"),
          hyperlink: () => {
            editPayment();
          },
          withIcon: true,
        }));

        // TODO: review logic for reinitiating payment intent. Do we just reinitiate the intent or create a new session?
        // reInitiatePaymentIntent();
        setSubmitting(true);
      });
  };

  // TODO: need to show donation amount in the review page.

  if (!payableSession.payable) {
    console.error("Payable not available in the session");
    return <Navigate to={routing.index()} />;
  }

  if (!payableSession.payableItemResponse) {
    console.error("Prepare endpoint response doesn't exist in the session");
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    dispatch({ type: EMPTY_WALLET_ITEMS });
    return <Navigate to={routing.payableDashboard.index()} />;
  }

  if (!paymentIntentState.paymentMethod) {
    console.error("Payment method doesn't exist in the payment intent state");
    return <Navigate to={routing.payableDashboard.index()} />;
  }

  const hasExtensionFields = Boolean(
    payableSession.payable.extensionFields &&
    Object.keys(payableSession.payable.extensionFields).length > 0,
  );

  const isEmailValid = !!values.email && errors.email == undefined;
  const isMobileNumberValid =
    errors.mobileNumber == undefined && values.mobileNumber != "";
  const hasActiveErrorBanner =
    bannerState.active && bannerState.variant === "error";
  const mobileNumberLabel = values.mobileNumber
    ? values.mobileNumber
    : "mobile number";
  const emailLabel = values.email ? values.email : "email";

  return (
    <div className="ez-pay-review-page">
      <div
        className="ez-pay-payable-banner"
        data-cy="ez-pay-review-banner"
        role={getBannerRole(bannerState.variant)}
      >
        <Banner
          {...bannerState}
          data-cy={
            bannerState?.variant
              ? `banner-${bannerState.variant}-content`
              : "banner-info-content"
          }
          onClose={() => setBannerState((prev) => ({ ...prev, active: false }))}
        ></Banner>
      </div>
      <div className="ez-pay-receipt-info-frame" data-cy="ez-pay-review-frame">
        <PageLoader />
        <div data-cy="ez-pay-review-top">
          <PayableCardHeader />

          {hasExtensionFields ? (
            <div
              className="ez-pay-review-top-row"
              data-cy="ez-pay-review-top-row"
            >
              {Object.entries(payableSession.payable.extensionFields || {}).map(
                ([fieldKey, field]) => (
                  <div
                    key={fieldKey}
                    className="ez-pay-extension-field"
                    data-cy={`ez-pay-review-extension-field-${fieldKey}`}
                  >
                    <div
                      className="ez-pay-extension-field-label"
                      data-cy={`ez-pay-review-extension-label-${fieldKey}`}
                    >
                      {getLabelWithKrn(
                        labelSourceWithKrn.labelsWithKrn,
                        payableSession?.payable?.typeKrn ?? null,
                        fieldKey,
                      )}
                      :
                    </div>{" "}
                    {getExtensionFieldValue(field)}
                  </div>
                ),
              )}
            </div>
          ) : null}

          <div data-cy="ez-pay-review-content">
            <div className="ez-pay-payable-row">
              <PayableReviewSummary />
              <hr className="ez-pay-review-separator" />
            </div>
            <form onSubmit={handleSubmit} autoComplete="off">
              {/* TODO: consider displaying the email and mobile number input fields for digital wallet payments
              with the values pre-filled from the receipt info set on PayableDashboard page instead of hiding them.
              */}
              {isDigitalWalletPayment ? null : (
                <>
                  <p
                    className="ez-pay-payable-row"
                    data-cy="ez-pay-review-instructions"
                  >
                    {t("review-info")}
                  </p>
                  <div
                    className="ez-pay-payable-row"
                    data-cy="ez-pay-review-input-container-email"
                  >
                    <InputField
                      name="email"
                      id="email"
                      errorText={getErrorText("email", touched, errors)}
                      placeholder="name@gmail.com"
                      label={t("email")}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasError={touched.email && errors.email != undefined}
                      dataCy="ez-pay-review-input-email"
                      aria-invalid={
                        Boolean(touched.email && !!errors.email) ||
                        hasActiveErrorBanner
                      }
                    />
                  </div>

                  <div
                    className="ez-pay-payable-row"
                    data-cy="ez-pay-review-input-container-mobile-number"
                  >
                    <InputField
                      name="mobileNumber"
                      id="mobileNumber"
                      errorText={getErrorText("mobileNumber", touched, errors)}
                      helperText={t("helper-text")}
                      label={t("mobile-number")}
                      placeholder="(xxx) xxx-xxxx"
                      value={values.mobileNumber}
                      onChange={handleMobileNumberChange}
                      onBlur={handleBlur}
                      hasError={
                        touched.mobileNumber && errors.mobileNumber != undefined
                      }
                      dataCy="ez-pay-review-input-mobile-number"
                      aria-invalid={
                        Boolean(
                          touched.mobileNumber && !!errors.mobileNumber,
                        ) || hasActiveErrorBanner
                      }
                    />
                  </div>
                </>
              )}

              {payableSession.payable.category.toLowerCase() === "account" &&
              !isDigitalWalletPayment ? (
                <div className="ez-pay-payable-row">
                  <div
                    className={
                      values.alertEnabled && errors.email == undefined
                        ? "receipt-info-alert-container-enabled"
                        : "receipt-info-alert-container"
                    }
                  >
                    <div className="payable-receipt-info-alert-title-toggle-container">
                      <span className="payable-receipt-info-alert-title">
                        {t("payment-due-alert-label", {
                          ns: RECEIPT_INFO_MODAL_NAMESPACE,
                        })}
                      </span>
                      <Toggle
                        label={`${t("payment-due-alert-label", {
                          ns: RECEIPT_INFO_MODAL_NAMESPACE,
                        })}`}
                        labelPosition="hidden"
                        onChange={handleToggleChange}
                        isCheckedDefault={
                          values.alertEnabled && errors.email == undefined
                        }
                        disabled={
                          values.email.length == 0 || errors.email != undefined
                        }
                        dataCy="toggle-payment-due-alert"
                      />
                    </div>
                    <div
                      className={
                        values.alertEnabled && errors.email == undefined
                          ? "payable-receipt-info-alert-line-separator-enabled"
                          : "payable-receipt-info-alert-line-separator"
                      }
                    ></div>
                    <div className="receipt-info-alert-text">
                      {t("payment-due-alert-text", {
                        ns: RECEIPT_INFO_MODAL_NAMESPACE,
                      })}
                    </div>
                    {values.alertEnabled && errors.email == undefined && (
                      <>
                        <div id="alertEnabled" style={{ width: "100%" }}>
                          <div className="payable-receipt-info-alert-line-separator-enabled"></div>
                          <br />
                          <div className="payable-receipt-info-checkbox-container">
                            <span
                              className={getCheckboxLabelClass(isEmailValid)}
                            >
                              {t("send-alerts-to-email", {
                                email: emailLabel,
                                ns: RECEIPT_INFO_MODAL_NAMESPACE,
                              })}
                            </span>
                            <Checkbox
                              name="emailAlert"
                              label=""
                              onChange={handleEmailAlertChange}
                              disabled={
                                !values.email || errors.email != undefined
                              }
                              defaultChecked={values.emailAlert}
                              dataCy="checkbox-email-alert"
                            />
                          </div>
                          <br />
                          <div className="payable-receipt-info-checkbox-container">
                            <span
                              className={getCheckboxLabelClass(
                                isMobileNumberValid,
                              )}
                            >
                              {t("send-alerts-to-mobile-number", {
                                mobileNumber: mobileNumberLabel,
                                ns: RECEIPT_INFO_MODAL_NAMESPACE,
                              })}
                            </span>
                            <Checkbox
                              name="mobileAlert"
                              label=""
                              onChange={handleMobileAlertChange}
                              disabled={
                                errors.mobileNumber != undefined ||
                                values.mobileNumber == ""
                              }
                              defaultChecked={
                                values.mobileAlert &&
                                !errors.mobileNumber &&
                                values.mobileNumber.length > 0
                              }
                              dataCy="checkbox-mobile-alert"
                            />
                          </div>
                        </div>
                        <div className="payable-receipt-info-alert-line-separator-enabled"></div>
                        <div className="receipt-info-mobile-helper-text-container">
                          <img
                            src={circleInfo}
                            alt="info icon"
                            className="infoIcon"
                          />
                          <span className="receipt-info-mobile-helper-text">
                            {values.mobileNumber
                              ? t("mobile-number-alerts-filled-help", {
                                  ns: RECEIPT_INFO_MODAL_NAMESPACE,
                                })
                              : t("mobile-number-alerts-empty-help", {
                                  ns: RECEIPT_INFO_MODAL_NAMESPACE,
                                })}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : null}

              <div
                className="ez-pay-payable-continue-button"
                data-cy="ez-pay-review-input-container-pay-button"
              >
                {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
                <Button
                  type="submit"
                  onClick={() => null}
                  className="validate-button"
                  data-cy="button-filled-pay"
                >
                  {t("pay-button-label", {
                    amount: currencyFormatter.format(
                      payableSession.payableItemResponse?.itemAndFeeTotalAmount,
                    ),
                  })}
                </Button>
              </div>
            </form>

            <p
              className="ez-pay-review-instructions-final"
              data-cy="ez-pay-review-instructions-final"
            >
              {t("review-instructions")}
              &nbsp;
              <TextButton
                hoverStyle="underline"
                onClick={() => {
                  window.open(KUBRA_TERMS_OF_USE_URL, "_blank", "noopener");
                }}
                className="terms-text"
                data-cy="ez-pay-review-terms-button"
              >
                {t("terms-of-use")}
              </TextButton>
              &nbsp;{t("and")}&nbsp;
              <TextButton
                hoverStyle="underline"
                onClick={() =>
                  window.open(KUBRA_PRIVACY_POLICY_URL, "_blank", "noopener")
                }
                className="terms-text"
                data-cy="ez-pay-review-privacy-button"
              >
                {t("privacy-policy")}
              </TextButton>
            </p>
            <ContactAndTocFooter biller={biller} />
          </div>
        </div>
      </div>
    </div>
  );
};
